import { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { templates as websiteTemplates, Template } from '../../mocks/websiteTemplates';
import Breadcrumb from '../../components/feature/Breadcrumb';
import ToolsDropdown from '../../components/feature/ToolsDropdown';
import LoginModal from '../home/components/LoginModal';
import TemplatePreviewModal from './components/TemplatePreviewModal';
import FeaturedTemplates from './components/FeaturedTemplates';
import CompareModal from './components/CompareModal';
import RecentlyViewed from './components/RecentlyViewed';
import FavoritesDrawer from './components/FavoritesDrawer';
import TemplateCollections from './components/TemplateCollections';
import TemplateOfTheWeek from './components/TemplateOfTheWeek';
import AIBuilderModal from './components/AIBuilderModal';
import ShareButton from './components/ShareButton';
import KeyboardShortcutsPanel from './components/KeyboardShortcutsPanel';
import OnboardingTour, { TOUR_STORAGE_KEY } from './components/OnboardingTour';
import { TemplateGridSkeleton } from './components/TemplateCardSkeleton';
import { CATEGORY_SLUGS, semanticScore, nameToSlug } from './websiteBuilderConstants';

type SortOption = 'popular' | 'newest' | 'az';

export default function WebsiteBuilder() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const [attachedFiles, setAttachedFiles] = useState<Array<{ file: File; preview?: string; id: string }>>([]);
  const [showAttachments, setShowAttachments] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const templatesPerPage = 9;
  const [showLogin, setShowLogin] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState<Template | null>(null);
  const [sortBy, setSortBy] = useState<SortOption>('popular');
  const [compareList, setCompareList] = useState<Template[]>([]);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [recentlyViewed, setRecentlyViewed] = useState<Template[]>([]);
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [favorites, setFavorites] = useState<Template[]>([]);
  const [showFavoritesDrawer, setShowFavoritesDrawer] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isAiMode, setIsAiMode] = useState(false);
  const [aiSearching, setAiSearching] = useState(false);
  const [activeCollectionId, setActiveCollectionId] = useState<string | null>(null);
  const [activeCollectionTemplates, setActiveCollectionTemplates] = useState<Template[]>([]);
  const [showAiBuilder, setShowAiBuilder] = useState(false);
  const [focusedCardIndex, setFocusedCardIndex] = useState<number | null>(null);
  const [showShortcutsPanel, setShowShortcutsPanel] = useState(false);
  const [showTour, setShowTour] = useState(false);

  // AI search debounce: shows "thinking" for 900ms then resolves
  useEffect(() => {
    if (!isAiMode || !searchQuery.trim()) {
      setAiSearching(false);
      return;
    }
    setAiSearching(true);
    const t = setTimeout(() => setAiSearching(false), 900);
    return () => clearTimeout(t);
  }, [isAiMode, searchQuery]);

  // Skeleton on initial mount
  useEffect(() => {
    const t = setTimeout(() => setIsLoading(false), 700);
    return () => clearTimeout(t);
  }, []);

  // Skeleton on filter/sort/page change
  useEffect(() => {
    setIsLoading(true);
    const t = setTimeout(() => setIsLoading(false), 450);
    return () => clearTimeout(t);
  }, [selectedCategory, selectedTag, sortBy, currentPage]);

  // Extract all unique tags from every template
  const ALL_TAGS = useMemo(() => {
    const tagSet = new Set<string>();
    websiteTemplates.forEach((t) => t.tags.forEach((tag) => tagSet.add(tag)));
    return Array.from(tagSet).sort();
  }, []);

  const categories = [
    'All', 'E-commerce', 'SaaS & Tech', 'Portfolio & Creative',
    'Restaurant & Food', 'Health & Wellness', 'Real Estate', 'Education',
    'Non-Profit', 'Fashion & Beauty', 'Travel & Hospitality', 'Finance & Legal',
    'Entertainment & Media',
  ];

  const SORT_OPTIONS: Array<{ value: SortOption; label: string; icon: string }> = [
    { value: 'popular', label: 'Most Popular', icon: 'ri-fire-line' },
    { value: 'newest', label: 'Newest', icon: 'ri-time-line' },
    { value: 'az', label: 'A → Z', icon: 'ri-sort-asc' },
  ];

  const badgeOrder: Record<string, number> = { Popular: 0, New: 1 };

  // Base pool: collection-filtered or all templates
  const basePool = activeCollectionTemplates.length > 0 ? activeCollectionTemplates : websiteTemplates;

  const filteredTemplates = useMemo(() => {
    let pool = basePool;

    // Tag filter
    if (selectedTag) pool = pool.filter((t) => t.tags.includes(selectedTag));

    // AI semantic search
    if (isAiMode && searchQuery.trim() && !aiSearching) {
      const scored = pool
        .map((t) => ({ t, score: semanticScore(t, searchQuery) }))
        .filter((s) => s.score > 0)
        .sort((a, b) => b.score - a.score);
      return scored.map((s) => s.t);
    }

    // Regular text search
    if (!isAiMode && searchQuery) {
      pool = pool.filter(
        (t) =>
          t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          t.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Sort
    return [...pool].sort((a, b) => {
      if (sortBy === 'newest') return new Date(b.dateAdded).getTime() - new Date(a.dateAdded).getTime();
      if (sortBy === 'az') return a.name.localeCompare(b.name);
      const aBadge = a.badge ? (badgeOrder[a.badge] ?? 2) : 2;
      const bBadge = b.badge ? (badgeOrder[b.badge] ?? 2) : 2;
      return aBadge - bBadge;
    });
  }, [basePool, selectedTag, isAiMode, searchQuery, aiSearching, sortBy]);

  const totalPages = Math.ceil(filteredTemplates.length / templatesPerPage);
  const startIndex = (currentPage - 1) * templatesPerPage;
  const currentTemplates = filteredTemplates.slice(startIndex, startIndex + templatesPerPage);

  const handleSelectCollection = useCallback((id: string | null, templates: Template[]) => {
    setActiveCollectionId(id);
    setActiveCollectionTemplates(templates);
    setCurrentPage(1);
    setSearchQuery('');
    setSelectedTag(null);
  }, []);

  const handleOpenPreview = (template: Template) => {
    setPreviewTemplate(template);
    setRecentlyViewed((prev) => {
      const filtered = prev.filter((t) => t.name !== template.name);
      return [template, ...filtered].slice(0, 10);
    });
  };

  const toggleCompare = (template: Template, e: React.MouseEvent) => {
    e.stopPropagation();
    setCompareList((prev) => {
      const exists = prev.find((t) => t.name === template.name);
      if (exists) return prev.filter((t) => t.name !== template.name);
      if (prev.length >= 2) return prev;
      return [...prev, template];
    });
  };

  const isInCompare = (template: Template) => compareList.some((t) => t.name === template.name);

  const toggleFavorite = (template: Template, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setFavorites((prev) => {
      const exists = prev.some((t) => t.name === template.name);
      return exists ? prev.filter((t) => t.name !== template.name) : [...prev, template];
    });
  };
  const isFavorited = (template: Template) => favorites.some((t) => t.name === template.name);

  const handleFileSelect = (files: FileList | null) => {
    if (!files) return;
    const newFiles: Array<{ file: File; preview?: string; id: string }> = [];
    const maxSize = 25 * 1024 * 1024;
    Array.from(files).forEach((file) => {
      if (attachedFiles.length + newFiles.length >= 10) return;
      if (file.size > maxSize) return;
      const fileType = file.type.split('/')[0];
      const fileExt = file.name.split('.').pop()?.toLowerCase();
      const validImageExts = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
      const validVideoExts = ['mp4', 'mov', 'avi', 'webm'];
      const validDocExts = ['pdf', 'docx', 'txt', 'csv'];
      if (
        (fileType === 'image' && validImageExts.includes(fileExt || '')) ||
        (fileType === 'video' && validVideoExts.includes(fileExt || '')) ||
        validDocExts.includes(fileExt || '')
      ) {
        const id = `${Date.now()}-${Math.random()}`;
        if (fileType === 'image') {
          const reader = new FileReader();
          reader.onload = (ev) => {
            newFiles.push({ file, preview: ev.target?.result as string, id });
            if (newFiles.length === Array.from(files).filter(f => {
              const ext = f.name.split('.').pop()?.toLowerCase();
              return validImageExts.includes(ext || '') || validVideoExts.includes(ext || '') || validDocExts.includes(ext || '');
            }).length) {
              setAttachedFiles(prev => [...prev, ...newFiles]);
            }
          };
          reader.readAsDataURL(file);
        } else {
          newFiles.push({ file, id });
        }
      }
    });
    if (newFiles.filter(f => !f.preview).length > 0) {
      setAttachedFiles(prev => [...prev, ...newFiles.filter(f => !f.preview)]);
    }
  };

  const removeFile = (id: string) => setAttachedFiles(prev => prev.filter(f => f.id !== id));

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileIcon = (fileName: string) => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    if (['mp4', 'mov', 'avi', 'webm'].includes(ext || '')) return 'ri-video-line';
    if (ext === 'pdf') return 'ri-file-pdf-line';
    if (['docx', 'txt', 'csv'].includes(ext || '')) return 'ri-file-text-line';
    return 'ri-file-line';
  };

  // Reset focused card when page / filter changes
  useEffect(() => {
    setFocusedCardIndex(null);
  }, [currentPage, sortBy, selectedTag, selectedCategory, searchQuery, activeCollectionId]);

  // Scroll focused card into view
  useEffect(() => {
    if (focusedCardIndex === null || !gridRef.current) return;
    const el = gridRef.current.querySelector<HTMLElement>(`[data-card-index="${focusedCardIndex}"]`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [focusedCardIndex]);

  // ── Keyboard shortcut system ──────────────────────────────────────
  // Use a ref to always read latest state inside the stable listener
  const kbRef = useRef({
    focusedCardIndex: null as number | null,
    currentTemplates: [] as Template[],
    currentPage: 1,
    searchQuery: '',
    selectedTag: null as string | null,
    selectedCategory: 'All',
    activeCollectionId: null as string | null,
    anyModalOpen: false,
  });

  useEffect(() => {
    kbRef.current = {
      focusedCardIndex,
      currentTemplates,
      currentPage,
      searchQuery,
      selectedTag,
      selectedCategory,
      activeCollectionId,
      anyModalOpen: !!previewTemplate || showCompareModal || showFavoritesDrawer || showLogin || showAiBuilder,
    };
  });

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const s = kbRef.current;
      if (s.anyModalOpen) return;

      const tag = (e.target as HTMLElement).tagName.toLowerCase();
      const isTyping = ['input', 'textarea', 'select'].includes(tag);
      const aiOffset = s.currentPage === 1 ? 1 : 0;
      const total = aiOffset + s.currentTemplates.length;

      /* ── "/" → focus search ── */
      if (e.key === '/' && !isTyping) {
        e.preventDefault();
        searchInputRef.current?.focus();
        setFocusedCardIndex(null);
        return;
      }

      /* ── Escape ── */
      if (e.key === 'Escape') {
        if (isTyping) { (e.target as HTMLElement).blur(); return; }
        if (s.focusedCardIndex !== null) { setFocusedCardIndex(null); return; }
        const hasFilters = s.searchQuery || s.selectedTag || s.selectedCategory !== 'All' || !!s.activeCollectionId;
        if (hasFilters) {
          setSearchQuery('');
          setSelectedTag(null);
          setSelectedCategory('All');
          setActiveCollectionId(null);
          setActiveCollectionTemplates([]);
          setCurrentPage(1);
        }
        return;
      }

      /* ── Arrow keys → grid navigation ── */
      if (!isTyping && ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
        e.preventDefault();
        if (total === 0) return;

        const cur = s.focusedCardIndex;
        if (cur === null) { setFocusedCardIndex(0); return; }

        const cols = 3;
        let next = cur;
        if (e.key === 'ArrowRight') next = Math.min(cur + 1, total - 1);
        if (e.key === 'ArrowLeft')  next = Math.max(cur - 1, 0);
        if (e.key === 'ArrowDown')  next = Math.min(cur + cols, total - 1);
        if (e.key === 'ArrowUp')    next = Math.max(cur - cols, 0);
        if (next !== cur) setFocusedCardIndex(next);
        return;
      }

      /* ── Enter → open focused card ── */
      if (e.key === 'Enter' && s.focusedCardIndex !== null && !isTyping) {
        e.preventDefault();
        if (s.currentPage === 1 && s.focusedCardIndex === 0) {
          setShowAiBuilder(true); setFocusedCardIndex(null); return;
        }
        const idx = s.focusedCardIndex - aiOffset;
        if (idx >= 0 && idx < s.currentTemplates.length) {
          handleOpenPreview(s.currentTemplates[idx]);
          setFocusedCardIndex(null);
        }
        return;
      }

      /* ── F → favorite focused card ── */
      if (e.key.toLowerCase() === 'f' && s.focusedCardIndex !== null && !isTyping) {
        e.preventDefault();
        const idx = s.focusedCardIndex - aiOffset;
        if (idx >= 0 && idx < s.currentTemplates.length) {
          toggleFavorite(s.currentTemplates[idx]);
        }
        return;
      }

      /* ── C → compare focused card ── */
      if (e.key.toLowerCase() === 'c' && s.focusedCardIndex !== null && !isTyping) {
        e.preventDefault();
        const idx = s.focusedCardIndex - aiOffset;
        if (idx >= 0 && idx < s.currentTemplates.length) {
          const t = s.currentTemplates[idx];
          setCompareList((prev) => {
            const exists = prev.find((item) => item.name === t.name);
            if (exists) return prev.filter((item) => item.name !== t.name);
            if (prev.length >= 2) return prev;
            return [...prev, t];
          });
        }
        return;
      }

      /* ── ? → toggle shortcuts panel ── */
      if (e.key === '?' && !isTyping) {
        setShowShortcutsPanel((v) => !v);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Show tour on first visit (after skeleton loads)
  useEffect(() => {
    if (!localStorage.getItem(TOUR_STORAGE_KEY)) {
      const t = setTimeout(() => setShowTour(true), 900);
      return () => clearTimeout(t);
    }
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button onClick={() => navigate('/')} className="cursor-pointer flex-shrink-0">
              <img
                src="https://static.readdy.ai/image/0de80e9fd237f0b8df79ee5619c3a653/49cc0aa73e52e5f4616af3a61a585458.jpeg"
                alt="1WORLD-AI Logo"
                className="h-12 w-auto object-contain"
              />
            </button>
            <div className="flex items-center gap-3">
              <ToolsDropdown isDarkMode={false} />
              {/* Favorites button */}
              <button
                onClick={() => setShowFavoritesDrawer(true)}
                className={`relative flex items-center gap-1.5 px-3 py-2 rounded-full border text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  favorites.length > 0
                    ? 'border-rose-200 bg-rose-50 text-rose-600 hover:bg-rose-100'
                    : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                }`}
              >
                <i className={`text-base ${favorites.length > 0 ? 'ri-heart-3-fill text-rose-500' : 'ri-heart-3-line'}`}></i>
                {favorites.length > 0 && <span>{favorites.length}</span>}
              </button>
              <button
                onClick={() => setShowLogin(true)}
                className="text-sm font-semibold px-4 py-2 rounded-full border border-gray-200 text-gray-700 hover:bg-gray-100 transition-all whitespace-nowrap cursor-pointer"
              >
                Sign In
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Breadcrumb */}
      <div className="fixed top-16 left-0 right-0 z-40">
        <Breadcrumb currentPage="Website Builder" variant="light" />
      </div>

      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-6 pt-20 pb-16">

        {/* Template of the Week — sticky spotlight banner */}
        {!searchQuery && selectedCategory === 'All' && !selectedTag && !activeCollectionId && (
          <TemplateOfTheWeek
            templates={websiteTemplates}
            onPreview={handleOpenPreview}
          />
        )}

        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-violet-50 border border-violet-200 mb-6">
            <div className="relative">
              <div className="w-2 h-2 bg-gradient-to-r from-violet-500 to-cyan-500 rounded-full animate-pulse"></div>
              <div className="absolute inset-0 w-2 h-2 bg-gradient-to-r from-violet-500 to-cyan-500 rounded-full animate-ping opacity-75"></div>
            </div>
            <span className="text-sm font-medium text-violet-700">OpenClaw Algorithm</span>
          </div>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-cyan-50 border border-cyan-200 mb-6 ml-3">
            <i className="ri-checkbox-circle-fill text-cyan-600 text-sm"></i>
            <span className="text-xs font-semibold text-cyan-700">15M+ Websites Generated</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-gray-900 via-violet-700 to-cyan-700 bg-clip-text text-transparent">
            Build Stunning Websites
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
            Choose from professional templates powered by OpenClaw AI web generation engine
          </p>

          {/* Search Bar */}
          <div data-tour="search" className="max-w-2xl mx-auto mb-8">
            <div className={`relative transition-all duration-300 ${isAiMode ? 'ring-2 ring-violet-400/40 rounded-xl' : ''}`}>
              {/* Left icon — sparkle when AI, search when normal */}
              <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center pointer-events-none">
                {isAiMode ? (
                  <i className={`ri-sparkling-2-fill text-lg ${aiSearching ? 'text-violet-400 animate-pulse' : 'text-violet-500'}`}></i>
                ) : (
                  <i className="ri-search-line text-gray-400 text-lg"></i>
                )}
              </div>

              <input
                ref={searchInputRef}
                type="text"
                placeholder={isAiMode ? 'Describe your ideal website... e.g., "dark portfolio for a photographer"' : 'Search templates...'}
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                className={`w-full pl-12 pr-28 py-4 bg-white border rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none transition-all text-sm shadow-sm ${
                  isAiMode
                    ? 'border-violet-400 focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20'
                    : 'border-gray-200 focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20'
                }`}
              />

              {/* Right controls */}
              <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                {/* AI mode toggle */}
                <button
                  onClick={() => { setIsAiMode((v) => !v); setSearchQuery(''); setCurrentPage(1); }}
                  title={isAiMode ? 'Switch to regular search' : 'Switch to AI semantic search'}
                  className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                    isAiMode
                      ? 'bg-violet-500 text-white shadow-sm'
                      : 'bg-gray-100 text-gray-500 hover:bg-violet-50 hover:text-violet-600'
                  }`}
                >
                  <i className="ri-sparkling-2-fill text-sm"></i>
                  AI
                </button>
                {/* Attachment */}
                <button
                  onClick={() => setShowAttachments(!showAttachments)}
                  className="w-9 h-9 flex items-center justify-center rounded-lg bg-gray-50 hover:bg-gray-100 text-gray-600 hover:text-gray-900 transition-all cursor-pointer relative"
                >
                  <i className="ri-attachment-line text-base"></i>
                  {attachedFiles.length > 0 && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-violet-500 text-white text-xs rounded-full flex items-center justify-center">
                      {attachedFiles.length}
                    </span>
                  )}
                </button>
              </div>
            </div>

            {/* AI search context banner */}
            {isAiMode && searchQuery && (
              <div className={`mt-2 px-4 py-2 rounded-lg flex items-center gap-2 text-xs transition-all ${
                aiSearching ? 'bg-violet-50 border border-violet-200' : 'bg-emerald-50 border border-emerald-200'
              }`}>
                {aiSearching ? (
                  <>
                    <i className="ri-sparkling-2-fill text-violet-500 animate-pulse"></i>
                    <span className="text-violet-700 font-medium">AI is finding the best matches for you...</span>
                  </>
                ) : (
                  <>
                    <i className="ri-checkbox-circle-fill text-emerald-500"></i>
                    <span className="text-emerald-700 font-medium">AI found <strong>{filteredTemplates.length}</strong> matches for &ldquo;{searchQuery}&rdquo;</span>
                    <button onClick={() => { setSearchQuery(''); setCurrentPage(1); }} className="ml-auto text-gray-400 hover:text-gray-700 cursor-pointer">
                      <i className="ri-close-line"></i>
                    </button>
                  </>
                )}
              </div>
            )}

            {showAttachments && (
              <div className="mt-3 bg-white border border-gray-200 rounded-xl p-4 shadow-lg">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-900">Attachments ({attachedFiles.length}/10)</h3>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="px-3 py-1.5 bg-violet-500 hover:bg-violet-600 text-white text-xs font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-add-line mr-1"></i>Add Files
                  </button>
                </div>

                {/* Drag & Drop Zone */}
                <div
                  onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={(e) => { e.preventDefault(); setIsDragging(false); handleFileSelect(e.dataTransfer.files); }}
                  className={`border-2 border-dashed rounded-lg p-6 text-center transition-all ${
                    isDragging ? 'border-violet-500 bg-violet-50' : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <i className={`ri-upload-cloud-line text-3xl mb-2 block ${isDragging ? 'text-violet-500' : 'text-gray-400'}`}></i>
                  <p className="text-sm text-gray-600 mb-1">Drag &amp; drop files here or click to browse</p>
                  <p className="text-xs text-gray-500">Images, Videos, Documents • Max 25MB per file</p>
                </div>

                {/* File List */}
                {attachedFiles.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {/* Image Previews */}
                    {attachedFiles.filter(f => f.preview).length > 0 && (
                      <div className="grid grid-cols-4 gap-2">
                        {attachedFiles.filter(f => f.preview).map((item) => (
                          <div key={item.id} className="relative group">
                            <img src={item.preview} alt={item.file.name} className="w-full h-24 object-cover rounded-lg border border-gray-200" />
                            <button onClick={() => removeFile(item.id)} className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                              <i className="ri-close-line text-sm"></i>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Other Files */}
                    {attachedFiles.filter(f => !f.preview).map((item) => (
                      <div key={item.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <i className={`${getFileIcon(item.file.name)} text-2xl text-gray-600 w-8 h-8 flex items-center justify-center`}></i>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{item.file.name}</p>
                          <p className="text-xs text-gray-500">{formatFileSize(item.file.size)}</p>
                        </div>
                        <button onClick={() => removeFile(item.id)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-200 text-gray-500 hover:text-red-600 transition-all cursor-pointer">
                          <i className="ri-close-line text-lg"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".jpg,.jpeg,.png,.gif,.webp,.mp4,.mov,.avi,.webm,.pdf,.docx,.txt,.csv"
                  onChange={(e) => handleFileSelect(e.target.files)}
                  className="hidden"
                />
              </div>
            )}
          </div>

          {/* Category Filter — navigate to category pages */}
          <div className="flex flex-wrap justify-center gap-3 mb-5">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => {
                  if (category === 'All') {
                    setSelectedCategory('All');
                    setCurrentPage(1);
                  } else {
                    navigate(`/website-builder/category/${CATEGORY_SLUGS[category]}`);
                  }
                }}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  selectedCategory === category
                    ? 'bg-violet-500 text-white shadow-lg shadow-violet-500/30'
                    : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                {category}
                {category !== 'All' && (
                  <i className="ri-arrow-right-s-line ml-0.5 text-xs opacity-50"></i>
                )}
              </button>
            ))}
          </div>

          {/* ── Tag Filter Chips ── */}
          <div className="flex flex-wrap justify-center gap-2">
            <button
              onClick={() => { setSelectedTag(null); setCurrentPage(1); }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap cursor-pointer border ${
                selectedTag === null
                  ? 'bg-gray-900 text-white border-gray-900'
                  : 'bg-white text-gray-500 border-gray-200 hover:border-gray-400 hover:text-gray-700'
              }`}
            >
              <i className="ri-apps-line text-xs"></i>
              All Styles
            </button>
            {ALL_TAGS.map((tag) => (
              <button
                key={tag}
                onClick={() => { setSelectedTag(selectedTag === tag ? null : tag); setCurrentPage(1); }}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap cursor-pointer border ${
                  selectedTag === tag
                    ? 'bg-gray-900 text-white border-gray-900'
                    : 'bg-white text-gray-500 border-gray-200 hover:border-gray-400 hover:text-gray-700'
                }`}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>

        {/* Featured Templates — only shown when no active filters */}
        {!searchQuery && selectedCategory === 'All' && !selectedTag && !activeCollectionId && (
          <FeaturedTemplates allTemplates={websiteTemplates} onPreview={handleOpenPreview} />
        )}

        {/* Recently Viewed strip */}
        {!searchQuery && selectedCategory === 'All' && !selectedTag && !activeCollectionId && (
          <RecentlyViewed templates={recentlyViewed} onPreview={handleOpenPreview} />
        )}

        {/* Collections section */}
        {!searchQuery && selectedCategory === 'All' && !selectedTag && (
          <TemplateCollections
            activeCollectionId={activeCollectionId}
            onSelectCollection={handleSelectCollection}
          />
        )}

        {/* Collection active banner */}
        {activeCollectionId && (
          <div className="mb-5 px-4 py-3 rounded-xl bg-gray-900 text-white flex items-center gap-3">
            <i className="ri-collection-fill text-base"></i>
            <p className="text-sm font-semibold flex-1">Viewing collection: <span className="text-violet-300">{activeCollectionId.split('-').map(w => w[0].toUpperCase() + w.slice(1)).join(' ')}</span></p>
            <button
              onClick={() => handleSelectCollection(null, [])}
              className="text-xs text-gray-400 hover:text-white flex items-center gap-1 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-close-line"></i>
              Exit Collection
            </button>
          </div>
        )}

        {/* Sort bar + result count */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <p className="text-sm text-gray-500">
              <span className="font-semibold text-gray-900">{filteredTemplates.length}</span> templates
              {selectedCategory !== 'All' && <span> in <span className="text-violet-600 font-medium">{selectedCategory}</span></span>}
              {selectedTag && <span> tagged <span className="text-gray-800 font-semibold">{selectedTag}</span></span>}
            </p>
            {(selectedTag || selectedCategory !== 'All' || searchQuery) && (
              <button
                onClick={() => { setSelectedTag(null); setSelectedCategory('All'); setSearchQuery(''); setCurrentPage(1); }}
                className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-700 transition-colors cursor-pointer"
              >
                <i className="ri-close-circle-line"></i>
                Clear all
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {compareList.length > 0 && (
              <div className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-50 border border-violet-200 rounded-lg text-xs text-violet-700 font-medium">
                <i className="ri-git-compare-line"></i>
                {compareList.length}/2 selected
              </div>
            )}
            <span className="text-xs text-gray-500 font-medium hidden sm:block">Sort by</span>
            <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1">
              {SORT_OPTIONS.map(({ value, label, icon }) => (
                <button
                  key={value}
                  onClick={() => { setSortBy(value); setCurrentPage(1); }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                    sortBy === value
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <i className={`${icon} text-xs`}></i>
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Templates Grid */}
        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {isLoading ? (
            <TemplateGridSkeleton count={templatesPerPage} />
          ) : (
            <>
              {/* AI Builder card — first slot on page 1 */}
              {currentPage === 1 && (
                <button
                  data-card-index="0"
                  data-tour="ai-card"
                  onClick={() => setShowAiBuilder(true)}
                  className={`group relative rounded-xl overflow-hidden cursor-pointer transition-all duration-300 hover:scale-[1.01] text-left flex flex-col ${
                    focusedCardIndex === 0 ? 'ring-2 ring-violet-500 ring-offset-2 scale-[1.01]' : ''
                  }`}
                  style={{
                    background: 'linear-gradient(135deg, #0c0c18 0%, #13082a 50%, #0a1421 100%)',
                    border: focusedCardIndex === 0
                      ? '1px solid rgba(124,58,237,0.7)'
                      : '1px solid rgba(124,58,237,0.28)',
                  }}
                >
                  {/* Image area */}
                  <div className="relative flex items-center justify-center overflow-hidden" style={{ aspectRatio: '16/9' }}>
                    {/* Ambient glows */}
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-40 rounded-full pointer-events-none"
                      style={{ background: 'radial-gradient(circle, rgba(124,58,237,0.35) 0%, transparent 70%)', filter: 'blur(20px)' }}></div>
                    <div className="absolute top-1/3 right-1/4 w-20 h-20 rounded-full pointer-events-none"
                      style={{ background: 'radial-gradient(circle, rgba(6,182,212,0.25) 0%, transparent 70%)', filter: 'blur(15px)' }}></div>

                    {/* Floating sparkles */}
                    <i className="ri-sparkling-2-fill absolute top-4 right-6 text-violet-400/40 text-sm animate-pulse"></i>
                    <i className="ri-sparkling-2-fill absolute bottom-5 left-5 text-cyan-400/30 text-xs animate-pulse" style={{ animationDelay: '400ms' }}></i>
                    <i className="ri-sparkling-fill absolute top-6 left-8 text-violet-300/25 text-xs animate-pulse" style={{ animationDelay: '800ms' }}></i>
                    <i className="ri-sparkling-fill absolute bottom-4 right-10 text-cyan-300/20 text-sm animate-pulse" style={{ animationDelay: '1200ms' }}></i>
                    <i className="ri-stars-fill absolute top-1/2 left-4 -translate-y-1/2 text-violet-400/15 text-lg animate-pulse" style={{ animationDelay: '600ms' }}></i>

                    {/* Orbit rings */}
                    <div className="absolute w-28 h-28 rounded-full pointer-events-none" style={{ border: '1px solid rgba(124,58,237,0.12)' }}></div>
                    <div className="absolute w-20 h-20 rounded-full pointer-events-none" style={{ border: '1px dashed rgba(6,182,212,0.10)' }}></div>

                    {/* Central icon */}
                    <div className="relative z-10 flex flex-col items-center gap-3">
                      <div
                        className="w-14 h-14 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300"
                        style={{ background: 'linear-gradient(135deg, #7c3aed, #06b6d4)', boxShadow: '0 0 28px rgba(124,58,237,0.45)' }}
                      >
                        <i className="ri-sparkling-2-fill text-white text-xl"></i>
                      </div>
                      <div className="text-center px-4">
                        <p className="text-white font-black text-sm leading-tight">Start from Scratch</p>
                        <p className="text-white/40 text-xs mt-0.5">AI designs it for you</p>
                      </div>
                    </div>

                    {/* Hover ring glow */}
                    <div className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
                      style={{ boxShadow: 'inset 0 0 40px rgba(124,58,237,0.12)' }}></div>
                  </div>

                  {/* Content footer */}
                  <div className="p-5 flex flex-col gap-3 flex-1" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                    <div>
                      <h3 className="text-sm font-bold text-white mb-1">AI-Generated Template</h3>
                      <p className="text-white/40 text-xs leading-relaxed line-clamp-2">
                        Describe your ideal website in plain English and our AI matches you with the best template from 335 options.
                      </p>
                    </div>

                    <div className="flex items-center justify-between mt-auto">
                      {/* Step indicators */}
                      <div className="flex items-center gap-1.5">
                        {['Describe', 'Style', 'Match'].map((s, i) => (
                          <div key={s} className="flex items-center gap-1">
                            <span className="w-4 h-4 rounded-full flex items-center justify-center font-bold text-white/60" style={{ background: 'rgba(124,58,237,0.3)', fontSize: 9 }}>{i + 1}</span>
                            <span className="text-white/30 text-xs">{s}</span>
                            {i < 2 && <i className="ri-arrow-right-s-line text-white/15 text-xs"></i>}
                          </div>
                        ))}
                      </div>
                      {/* CTA pill */}
                      <div
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-white whitespace-nowrap"
                        style={{ background: 'linear-gradient(135deg,#7c3aed,#06b6d4)' }}
                      >
                        <i className="ri-sparkling-2-line text-xs"></i>
                        Try AI
                      </div>
                    </div>
                  </div>
                </button>
              )}

              {currentTemplates.map((template, index) => {
                const inCompare = isInCompare(template);
                const compareDisabled = compareList.length >= 2 && !inCompare;
                const faved = isFavorited(template);
                const aiOffset = currentPage === 1 ? 1 : 0;
                const cardIndex = index + aiOffset;
                const isFocused = focusedCardIndex === cardIndex;

                return (
                  <div
                    key={`${template.name}-${index}`}
                    data-card-index={cardIndex}
                    className={`group bg-white border rounded-xl overflow-hidden transition-all duration-300 ${
                      isFocused
                        ? 'border-violet-500 ring-2 ring-violet-400/60 ring-offset-1 scale-[1.01]'
                        : inCompare
                          ? 'border-violet-500 ring-2 ring-violet-400/40'
                          : 'border-gray-200 hover:border-violet-400 hover:shadow-xl'
                    }`}
                  >
                    <div className="aspect-video bg-gray-100 relative overflow-hidden">
                      <img
                        src={template.image}
                        alt={template.name}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                      {/* Top badges */}
                      <div className="absolute top-3 right-3 flex gap-2">
                        {template.badge && (
                          <span className={`px-3 py-1 backdrop-blur-sm text-white text-xs font-medium rounded-full ${
                            template.badge === 'Popular' ? 'bg-amber-500/90' : 'bg-emerald-500/90'
                          }`}>
                            {template.badge}
                          </span>
                        )}
                        <span className="px-3 py-1 bg-violet-500/90 backdrop-blur-sm text-white text-xs font-medium rounded-full">
                          {template.category}
                        </span>
                      </div>

                      {/* Heart favorite button — top-left */}
                      <button
                        onClick={(e) => toggleFavorite(template, e)}
                        className={`absolute top-3 left-3 w-7 h-7 rounded-lg flex items-center justify-center transition-all cursor-pointer ${
                          faved
                            ? 'bg-rose-500 text-white opacity-100'
                            : 'bg-white/80 text-gray-400 hover:text-rose-500 hover:bg-white opacity-0 group-hover:opacity-100'
                        }`}
                        title={faved ? 'Remove from favorites' : 'Save to favorites'}
                      >
                        <i className={`text-sm ${faved ? 'ri-heart-3-fill' : 'ri-heart-3-line'}`}></i>
                      </button>

                      {/* Compare checkbox */}
                      <button
                        onClick={(e) => toggleCompare(template, e)}
                        disabled={compareDisabled}
                        title={compareDisabled ? 'Remove one to select another' : inCompare ? 'Remove from compare' : 'Add to compare'}
                        className={`absolute bottom-3 left-3 w-7 h-7 rounded-lg flex items-center justify-center transition-all cursor-pointer ${
                          inCompare
                            ? 'bg-violet-600 text-white'
                            : compareDisabled
                              ? 'bg-white/50 text-gray-300 cursor-not-allowed'
                              : 'bg-white/80 text-gray-500 hover:bg-white hover:text-violet-600 opacity-0 group-hover:opacity-100'
                        }`}
                      >
                        {inCompare
                          ? <i className="ri-checkbox-fill text-sm"></i>
                          : <i className="ri-checkbox-blank-line text-sm"></i>
                        }
                      </button>

                      {/* Hover overlay */}
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleOpenPreview(template)}
                          className="flex items-center gap-1.5 px-3 py-2 bg-white text-gray-900 text-xs font-semibold rounded-full hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-eye-line"></i>
                          Preview
                        </button>
                        <button
                          onClick={() => navigate(`/website-builder/template/${nameToSlug(template.name)}`)}
                          className="flex items-center gap-1.5 px-3 py-2 bg-white/20 backdrop-blur-sm text-white text-xs font-semibold rounded-full hover:bg-white/30 transition-colors cursor-pointer whitespace-nowrap border border-white/40"
                        >
                          <i className="ri-file-info-line"></i>
                          Details
                        </button>
                        <ShareButton
                          url={`${window.location.origin}/website-builder/template/${nameToSlug(template.name)}`}
                          variant="overlay"
                        />
                      </div>
                    </div>

                    <div className="p-5">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">{template.name}</h3>
                      <p className="text-gray-600 text-sm mb-4 line-clamp-2">{template.description}</p>
                      <div className="flex items-center justify-between">
                        <div className="flex flex-wrap gap-1">
                          {template.tags.slice(0, 2).map((tag) => (
                            <button
                              key={tag}
                              onClick={() => { setSelectedTag(tag === selectedTag ? null : tag); setCurrentPage(1); }}
                              className={`px-2 py-0.5 text-xs rounded-full transition-all cursor-pointer whitespace-nowrap ${
                                selectedTag === tag
                                ? 'bg-gray-900 text-white'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                            >
                              {tag}
                            </button>
                          ))}
                          <span className="px-2 py-0.5 bg-gradient-to-r from-violet-500 to-cyan-500 text-white text-xs rounded-full font-medium">
                            openclaw.ai
                          </span>
                        </div>
                        <button
                          onClick={() => handleOpenPreview(template)}
                          className="px-4 py-2 bg-violet-500 hover:bg-violet-600 text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                        >
                          Use Template
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>

        {/* Empty State */}
        {currentTemplates.length === 0 && (
          <div className="text-center py-20">
            <i className="ri-search-line text-5xl text-gray-300 mb-4 block"></i>
            <p className="text-gray-500 text-lg">No templates found for your search.</p>
            <button
              onClick={() => { setSearchQuery(''); setSelectedCategory('All'); setSelectedTag(null); }}
              className="mt-4 px-6 py-2 bg-violet-500 hover:bg-violet-600 text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer"
            >
              Clear Filters
            </button>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 flex-wrap">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-700 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-s-line"></i>
            </button>

            {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
              const page = currentPage <= 4
                ? i + 1
                : currentPage >= totalPages - 3
                  ? totalPages - 6 + i
                  : currentPage - 3 + i;
              return Math.max(1, Math.min(totalPages, page));
            }).filter((p, i, arr) => arr.indexOf(p) === i).map((page) => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer ${
                  currentPage === page
                    ? 'bg-violet-500 text-white shadow-lg shadow-violet-500/30'
                    : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                }`}
              >
                {page}
              </button>
            ))}

            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-700 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-right-s-line"></i>
            </button>
          </div>
        )}
      </div>

      {/* ── Floating Compare Bar ── */}
      {compareList.length > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-4 bg-gray-900 text-white px-5 py-3 rounded-2xl shadow-2xl">
          {/* Thumbnails */}
          <div className="flex items-center gap-2">
            {compareList.map((t, i) => (
              <div key={t.name} className="relative">
                <div className="w-10 h-10 rounded-lg overflow-hidden border-2 border-white/30">
                  <img src={t.image} alt={t.name} className="w-full h-full object-cover object-top" />
                </div>
                <button
                  onClick={(e) => toggleCompare(t, e)}
                  className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-gray-600 hover:bg-red-500 text-white rounded-full flex items-center justify-center transition-colors cursor-pointer"
                >
                  <i className="ri-close-line" style={{ fontSize: 9 }}></i>
                </button>
                {compareList.length === 1 && i === 0 && (
                  <div className="absolute -right-6 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full border-2 border-dashed border-white/40 bg-gray-700 flex items-center justify-center">
                    <i className="ri-add-line text-white/60" style={{ fontSize: 10 }}></i>
                  </div>
                )}
              </div>
            ))}
          </div>

          {compareList.length === 1 ? (
            <p className="text-sm text-gray-300">Select 1 more to compare</p>
          ) : (
            <>
              <div className="w-px h-6 bg-white/20"></div>
              <button
                onClick={() => setShowCompareModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-white text-gray-900 text-sm font-bold rounded-xl hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-git-compare-line"></i>
                Compare Now
              </button>
            </>
          )}
          <button
            onClick={() => setCompareList([])}
            className="text-gray-400 hover:text-white transition-colors cursor-pointer ml-1"
            title="Clear selection"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>
      )}

      {/* Login Modal */}
      {showLogin && <LoginModal onClose={() => setShowLogin(false)} />}

      {/* Template Preview Modal */}
      {previewTemplate && (
        <TemplatePreviewModal
          template={previewTemplate}
          onClose={() => setPreviewTemplate(null)}
        />
      )}

      {/* Compare Modal */}
      {showCompareModal && compareList.length === 2 && (
        <CompareModal
          templateA={compareList[0]}
          templateB={compareList[1]}
          onClose={() => setShowCompareModal(false)}
        />
      )}

      {/* Favorites Drawer */}
      {showFavoritesDrawer && (
        <FavoritesDrawer
          favorites={favorites}
          onClose={() => setShowFavoritesDrawer(false)}
          onRemove={toggleFavorite}
          onPreview={handleOpenPreview}
          onClearAll={() => setFavorites([])}
        />
      )}

      {/* Keyboard Shortcuts Panel */}
      <KeyboardShortcutsPanel
        show={showShortcutsPanel}
        onToggle={() => setShowShortcutsPanel((v) => !v)}
        focusedIndex={focusedCardIndex}
      />

      {/* AI Builder Modal */}
      {showAiBuilder && (
        <AIBuilderModal
          templates={websiteTemplates}
          onClose={() => setShowAiBuilder(false)}
          onPreview={handleOpenPreview}
        />
      )}

      {/* Onboarding Tour */}
      {showTour && (
        <OnboardingTour onDone={() => setShowTour(false)} />
      )}
    </div>
  );
}